import React, { useState } from 'react'
import './pit.scss';
import AccordionContainer from '../../common/accordion-container';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { CancelIcon, RefreshIcon, ReportIcon, SearchIcon } from '../../common/icons';
import useToggle from '../../../hook/useToggle';
import { ICategory, IOrg, getOrgName, isCategory } from '../../../types/org';
import OrgSelectionModal from '../../user/org-selection-modal';
import { IReportParamsInput } from '../../../types/report';
import moment from 'moment';
import { Language, ReportType } from '../../../types/constants';
import useReport from '../../../hook/useReport';
import SpinnerOverlay from '../../common/spinner/spinner';
import confirm from '../../common/confirm';

const initPiTParams = {
    language: Language.EN, 
    piTDate: moment(new Date()).format('YYYY-MM-DD'),
    includePosition: true,
    includeCoordinator: 0
}

const PiT = () => {
    const { isOpen: isModalOpen, toggle: toggleModal } = useToggle();

    const [piTParams, setPiTParams] = useState<Partial<IReportParamsInput>>(initPiTParams);

    const [org, setOrg] = useState<IOrg>();

    const [orgError, setOrgError] = useState<string>();
    const [pitDateError, setPitDateError] = useState<string>();
    const [typeError, setTypeError] = useState<string>();

    const { generateReport, inProgress } = useReport()

    const today = moment(new Date()).format('MM/DD/YYYY');

    const handleSubmit = (event: any) => {
        event.preventDefault();
        console.log("piTParams", piTParams)

        if (!piTParams.orgId) {
            setOrgError("Please select an organization.")
        }

        if (!piTParams.piTDate) {
            setPitDateError("Please select the date.")
        }
        if (!piTParams.reportType) {
            setTypeError("Please select the report type.")
        }
        if (piTParams.orgId && piTParams.piTDate && piTParams.reportType) {
            generateReport(piTParams as IReportParamsInput)

            confirm({
                title: 'Your report is downloading.',
                content: `Your report is being generated. When it's ready, your browser download manager will open so that you can access the report. If you can't find the file, check your download folder. <br /> <br /> Depending on the report size, it may take several minutes to generate and download.`,
                okText: <><CancelIcon/> Close</>,
                okBtnColor: 'secondary',
                titleCenter: true,
                bodyCenter: false,
                showCancel: false,
            })
        }

    }

    const handleReset = () => {
        setPiTParams(initPiTParams)
        setOrg(undefined)
        setOrgError("")
        setPitDateError("")
        setTypeError("")
        
    }

    const onOrgSelected = (org: ICategory | IOrg | undefined) => {
        if (!org || (isCategory(org))) return;
        setOrgError("")
        setOrg(org)
        setPiTParams({...piTParams, orgId: String(org.id)})
    }

    const onLanguageChange = (event: any) => {
        setPiTParams({...piTParams, language: event.target.value})
    }

    const onDateSelected = (event: any) => {
        const selectedDate = new Date(event.target.value);
        const minDate = new Date('2014-10-30'); 
        if (selectedDate < minDate) {
            setPitDateError("No data is available prior to 30 October 2014, please review your date.")
        } else {
            if (event.target.value) {
                setPitDateError("")
            }
            setPiTParams({...piTParams, piTDate: event.target.value})
        }
    }

    const onReportTypeChange = (event: any) => {
        if (event.target.value) {
            setTypeError("")
        }
        setPiTParams({...piTParams, reportType: event.target.value==='DETAILS' ? ReportType.RPT_PIT_DETAILS : event.target.value==='HIERARCHY' ? ReportType.RPT_PIT_HIERARCHY: undefined})
    }

    const onIncludePosChange = (event: any) => {
        setPiTParams({...piTParams, includePosition: event.target.checked})
    }

    const onIncludeCoordNameChange = (event: any) => {
        if (event.target.checked) {
            setPiTParams({...piTParams, piTDate: moment(new Date()).format('YYYY-MM-DD'), includeCoordinator: 1})
        } else {
            setPiTParams({...piTParams, includeCoordinator: 0})
        }
    }

    const onIncludeCoordEmailChange = (event: any) => {
        setPiTParams({...piTParams, includeCoordinator: event.target.checked ? 2 : 1})
    }

    console.log("render PiT")

    return (<>
        <AccordionContainer title="Report Parameters">
            <Form onSubmit={handleSubmit} onReset={handleReset}>

                <Row>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="org" sm={4}>Select Organization:</Label>
                            <Col sm={8} style={{display: 'flex', alignItems: 'baseline'}}>
                                <Button color="primary" outline className="me-3" style={{flex: '0 0 100px'}} onClick={toggleModal}>
                                    <SearchIcon /> Search
                                </Button>
                                <div style={{flex: '1'}}>{org 
                                    ? <>{getOrgName(org)} <span className="org-date">(Created: {org.createDate})</span></>
                                    : !orgError && 'No organization selected.'} </div>

                                {orgError && <div className="invalid-feedback" style={{display:'block'}}>{orgError}</div>}
                                <OrgSelectionModal isOpen={isModalOpen} supportAll={false} toggle={toggleModal} onSelect={onOrgSelected} />
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="language" sm={4}>Language:</Label>
                            <Col sm={8} style={{paddingTop: '7px'}}>
                                <FormGroup check inline>
                                    <Label>
                                        <Input type="radio" name="language" value="EN" checked={piTParams?.language === 'EN'}    
                                            onChange = {(event: any) => onLanguageChange(event)}/>
                                        English
                                    </Label>
                                </FormGroup>
                                <FormGroup check inline>
                                    <Label>
                                        <Input type="radio" name="language" value="FR" checked={piTParams?.language === 'FR'}
                                            onChange = {(event: any) => onLanguageChange(event)}/>
                                        French
                                    </Label>
                                </FormGroup>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        {!piTParams.includeCoordinator
                            ?
                            <FormGroup row>
                                <Label for="pitDate" sm={4}>Date:</Label>
                                <Col sm={8}>
                                    <Input id="pitDate" name="date" type="date" value={piTParams?.piTDate} onChange={onDateSelected} invalid={!!pitDateError}/>
                                    <FormFeedback>{pitDateError}</FormFeedback>
                                </Col>
                            </FormGroup>
                            :
                            <Row>
                                <Col sm={4} className="col-form-label"><span>Date:</span></Col>
                                <Col sm={8} className="col-form-label"><span>{today}</span></Col>
                            </Row>
                        }
                    </Col>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="reportType" sm={4}>Report Type:</Label>
                            <Col sm={8}>
                                <Input type="select" id="reportType" name="reportType"
                                    onChange={onReportTypeChange} defaultValue="" invalid={!!typeError}>
                                    <option value="" disabled selected hidden>Select report type</option>
                                    <option value=""></option>
                                    <option value="DETAILS">Organization Details Report</option>
                                    <option value="HIERARCHY">Organization Hierarchy Report</option>
                                </Input>
                                <FormFeedback>{typeError}</FormFeedback>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                
                {piTParams.reportType === ReportType.RPT_PIT_DETAILS &&
                <Row>
                    <Col>
                        <section style={{backgroundColor: '#EEE', padding: '1rem'}} >
                            <h3 style={{fontSize: '1.1rem'}}>Organization Details Report Display Fields</h3>

                            <FormGroup check>
                                <Input type="checkbox" id="includePos" name="includePos"
                                    checked={!!piTParams?.includePosition} onChange={onIncludePosChange} />
                                {' '}
                                <Label for="includePos">Include Positions/Roles & Individuals</Label>
                            </FormGroup>
                        </section>
                    </Col>
                </Row>
                }

                {piTParams.reportType === ReportType.RPT_PIT_HIERARCHY &&
                <Row>
                    <Col>
                        <section style={{backgroundColor: '#EEE', padding: '1rem'}} >
                            <h3 style={{fontSize: '1.1rem'}}>Organization Hierarchy Report Display Fields</h3>

                            <FormGroup row check className="d-flex">
                                <Col sm={6}>
                                    <Input type="checkbox" id="coordName" name="coordName"
                                        checked={!!piTParams.includeCoordinator && piTParams?.includeCoordinator > 0} onChange={onIncludeCoordNameChange} />
                                    {' '}
                                    <Label for="coordName">Coordinator's Name</Label>
                                </Col>
                                <Col sm={6}>
                                    <Input type="checkbox" id="coordEmail" name="coordEmail"
                                        disabled={!piTParams?.includeCoordinator}
                                        checked={!!piTParams.includeCoordinator && piTParams?.includeCoordinator > 1} onChange={onIncludeCoordEmailChange} />
                                    {' '}
                                    <Label for="coordEmail">Coordinator's Email</Label>
                                </Col>
                            </FormGroup>
                        </section>
                    </Col>
                </Row>
                }

                <Row className="pt-3">
                    <Col className="d-flex">
                        <Button type="submit" color="primary" className=" me-2">
                            <ReportIcon className="me-2"/> Generate Report
                        </Button>

                        <Button type="reset" color="secondary" className="action-link">
                            <RefreshIcon /> Reset
                        </Button>
                    </Col>
                </Row>

            </Form>
        </AccordionContainer>
        </>
    )
}

export default PiT
